import { get } from '../utils'
import { WINDOW } from '../constants/environment'
import { GEO_ERRORS } from '../constants/errors'
import { getGeocoder } from './google-maps'

const firstOfType = (type, arr) => arr.find(({ types }) => types.indexOf(type) !== -1)

const parseGeocoderResults = (results = []) => {
  const { address_components: addressComponents = [] } = results[0]
  const locality = firstOfType('locality', addressComponents)
  const state = firstOfType('administrative_area_level_1', addressComponents)
  const postcode = firstOfType('postal_code', addressComponents)
  return {
    ...(state ? {
      state: state.short_name
    } : {}),
    ...(locality ? {
      locality: locality.short_name
    } : {}),
    ...(postcode ? {
      postcode: postcode.short_name
    } : {})
  }
}

const geolocation = get('navigator.geolocation', WINDOW || {})

export const getErrorMessage = error => GEO_ERRORS[error.message || error] || GEO_ERRORS.default

const geocode = async (...args) => {
  let geocoder
  let statusCodes

  try {
    const googleGeocode = await getGeocoder()
    geocoder = googleGeocode.geocoder
    statusCodes = googleGeocode.statusCodes
  } catch (error) {
    throw new Error('NO_GEOCODER')
  }

  return new Promise((resolve, reject) => {
    geocoder.geocode(...args, (results, status) => {
      if (status !== statusCodes.OK) reject(`GEOCODER_${status}`)
      resolve(results)
    })
  })
}

export const getCurrentPosition = (options) => {
  if (!geolocation) throw new Error('NO_GEOLOCATION')

  return new Promise((resolve, reject) => {
    geolocation.getCurrentPosition(resolve, ({
      code
    }) => reject(`GEOLOCATION_${code}`), options)
  })
}

export const fetchGeoDataFromPostcode = (newPostcode) => {
  return geocode({
    address: `Postcode ${newPostcode}`,
    componentRestrictions: {
      country: 'NZ'
    }
  }).then(async (results) => {
    const {
      state,
      locality,
      postcode = newPostcode
    } = parseGeocoderResults(results)
    if (!state) throw new Error('POSTCODE_NOT_FOUND')
    return { state, locality, postcode }
  }).catch(() => {
    throw new Error('POSTCODE_NOT_FOUND')
  })
}

export const fetchGeoDataFromPosition = async ({
  latitude,
  longitude
}) => {
  try {
    const results = await geocode({
      location: {
        lat: Number(latitude),
        lng: Number(longitude)
      }
    })

    const {
      state,
      locality,
      postcode
    } = parseGeocoderResults(results)
    if (!state || !postcode) throw new Error('LOCATION_NOT_FOUND')

    return {
      state,
      locality,
      postcode
    }
  } catch (error) {
    throw error
  }
}

export const fetchGeoDataFromAddress = async (address) => {
  const getAddressComponent = (components, componentType) => {
    for (const component of components) {
      for (const type of component.types) {
        if (type === componentType) return component.short_name
      }
    }
  }

  try {
    const [place] = await geocode(address)
    return {
      addressDetails: {
        subpremise: getAddressComponent(place.address_components, 'subpremise'),
        number: getAddressComponent(place.address_components, 'street_number'),
        street: getAddressComponent(place.address_components, 'route'),
        state: getAddressComponent(place.address_components, 'administrative_area_level_1'),
        postcode: getAddressComponent(place.address_components, 'postal_code'),
        suburb: getAddressComponent(place.address_components, 'locality'),
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      },
      placeId: place.place_id
    }
  } catch (error) {
    throw error
  }
}



// WEBPACK FOOTER //
// ./src/apis/geo.js